<script lang="ts">
  import { _ } from "svelte-i18n";
  import type { ClientLogoItem } from "$lib/types/content";

  let { items = [] }: { items: ClientLogoItem[] } = $props();

  let itemsPerRow = $derived(Math.ceil(items.length / 3));
  let logoRows = $derived([
    items.slice(0, itemsPerRow),
    items.slice(itemsPerRow, itemsPerRow * 2),
    items.slice(itemsPerRow * 2),
  ]);
</script>

<section
  id="creative-marquee"
  aria-labelledby="creative-marquee-title"
  class="client-wall relative -mt-px overflow-x-clip bg-brand-light py-10 sm:py-12 lg:py-14 text-brand-dark"
>
  <div class="site-shell relative z-10 mb-6 sm:mb-8 text-center">
    <div>
      <p class="mb-4 font-sans text-sm font-medium text-brand-dark/75">
        {$_("sectionLabels.clients")}
      </p>
      <h2
        id="creative-marquee-title"
        class="font-sans text-[clamp(2.5rem,4.5vw,4.5rem)] font-semibold leading-[1.05] tracking-[-0.045em]"
      >
        {$_('home.creativeMarquee.title')}
      </h2>
    </div>
  </div>

  <div
    data-scroll-visual
    class="logo-rows relative z-10 border-y border-brand-dark/10"
  >
    {#each logoRows as row, rowIndex (`logo-row-${rowIndex}`)}
      <div
        class="logo-row overflow-hidden border-b border-brand-dark/10 last:border-b-0"
      >
        <div
          class:reverse={rowIndex === 1}
          class="logo-track flex w-max will-change-transform"
        >
          {#each [0, 1] as repeatIndex (repeatIndex)}
            <div class="flex" aria-hidden={repeatIndex === 1}>
              {#each row as client (client.id)}
                <div
                  class="logo-slot flex h-20 w-[12rem] shrink-0 items-center justify-center border-r border-brand-dark/10 px-6 sm:h-24 sm:w-[15rem]"
                >
                  {#if client.logoSrc && client.width && client.height}
                    <img
                      src={client.logoSrc}
                      alt={client.label}
                      width={client.width}
                      height={client.height}
                      loading="lazy"
                      class={`object-contain opacity-80 grayscale-[20%] transition-all duration-300 ${
                        [
                          "julie-benedetto-photography",
                          "maddy-pollard",
                          "manning-cartell",
                        ].includes(client.id)
                          ? "max-h-20 max-w-[11rem] scale-[1.15] sm:max-h-24 sm:max-w-[13rem]"
                          : "max-h-14 max-w-[9.5rem] sm:max-h-16 sm:max-w-[12rem]"
                      }`}
                    />
                  {:else}
                    <span class="placeholder-mark" aria-label={client.label}>
                      <span>{client.shortLabel}</span>
                      <small>Approved mark</small>
                    </span>
                  {/if}
                </div>
              {/each}
            </div>
          {/each}
        </div>
      </div>
    {/each}
  </div>
</section>

<style>
  .client-wall::before {
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse 72% 110% at 50% 0%,
      color-mix(in srgb, var(--color-brand-green) 30%, transparent),
      transparent 68%
    );
    content: "";
    pointer-events: none;
  }

  .logo-track {
    animation: logo-drift 42s linear infinite;
  }

  .logo-track.reverse {
    animation-direction: reverse;
    animation-duration: 48s;
  }

  .placeholder-mark {
    display: grid;
    gap: 0.35rem;
    color: rgb(51 46 45 / 0.42);
    font-family: var(--font-sans);
    font-size: clamp(1.75rem, 2.4vw, 2.6rem);
    font-weight: 500;
    line-height: 1.05;
    text-align: center;
    letter-spacing: -0.04em;
    transition:
      color 250ms ease,
      transform 250ms ease;
  }

  .placeholder-mark small {
    font-family: var(--font-sans);
    font-size: 0.75rem;
    font-style: normal;
    letter-spacing: 0;
  }

  .logo-slot:hover .placeholder-mark {
    color: var(--color-brand-green);
    transform: translateY(-2px);
  }

  .logo-slot:hover img {
    filter: grayscale(0);
    opacity: 0.82;
    transform: scale(1.04);
  }

  @keyframes logo-drift {
    to {
      transform: translateX(-50%);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .logo-track {
      animation: none;
    }
  }
</style>
